import {
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import type {ActionMeta, GroupBase, Props, SelectInstance} from 'react-select';
import {SelectOptionType} from '@guestapp/sdk';
import {ErrorType} from '../../../utils/types';
import {ReactSelect} from './styled';
import {customFilter, isGroup, isSingleValue} from './utils';
import {useTimeoutRef} from 'hooks/common';
import {
  Control,
  DropdownIndicator,
  Input,
  LoadingIndicator,
  Menu,
  MenuList,
  Placeholder,
  Option,
} from './components';
import {IconNames} from '@guestapp/ui';

type SelectRefType<T = undefined, V = string, L = string> = SelectInstance<
  SelectOptionType<T, V, L>,
  false,
  GroupBase<SelectOptionType<T, V, L>>
>;

type SelectCustomProps<T, V, L> = {
  onChange?: (
    option: SelectOptionType<T, V, L> | null,
    info: ActionMeta<SelectOptionType<T, V, L>>,
  ) => void;
  onHandleKeyDown?: (event: KeyboardEvent) => void;
  label?: string | null;
  invalid?: boolean;
  disabled?: boolean;
  trimFilterBy?: number;
  error?: ErrorType;
  clearValue?: (name: string) => void;
  leftIcon?: ReactNode;
  empty?: boolean;
  indicator?: IconNames;
  indicatorSize?: number;
  loading?: boolean;
  mobileVersion?: boolean;
  tooltip?: string;
  placeholder?: string;
  forcedSearchMode?: boolean;
  isUSAFormat?: boolean;
  isFocused?: boolean;
};

export type SelectProps<T = undefined, V = string, L = string> = Props<
  SelectOptionType<T, V, L>,
  false,
  GroupBase<SelectOptionType<T, V, L>>
> &
  SelectCustomProps<T, V, L>;

function SelectInner<T, V, L>(
  {
    onChange,
    label,
    invalid,
    error,
    className,
    disabled,
    loading,
    components,
    onMenuOpen,
    onMenuClose,
    empty,
    onHandleKeyDown,
    ...props
  }: SelectProps<T, V, L>,
  ref: ForwardedRef<SelectRefType<T, V, L>>,
) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isSearchableEnabled = (props.options?.length || 0) > 15;
  const needShowScrollbar = (props.options?.length || 0) > 8;
  const {mobileVersion} = props;
  const focusTimeoutRef = useTimeoutRef();
  const selectRef = useRef<SelectRefType<T, V, L>>(null);
  const handleChange = (
    option: SelectOptionType<T, V, L> | null,
    info: ActionMeta<SelectOptionType<T, V, L>>,
  ) => {
    if (disabled) return;
    onChange?.(option, info);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (disabled) return;
    onHandleKeyDown?.(event);
  };

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  useImperativeHandle(ref, () => selectRef.current as SelectRefType<T, V, L>, []);

  useEffect(
    function autoFocusAfterTimeout() {
      if (!mobileVersion) return;
      focusTimeoutRef.current = setTimeout(() => {
        selectRef.current?.focus();
        clearTimeout(focusTimeoutRef.current);
      }, 400);
    },
    [focusTimeoutRef, mobileVersion],
  );

  useEffect(
    function setCorrectOptionIfThereIsOnlyValue() {
      const value = props.value;
      if (
        isSingleValue(value) &&
        value?.value &&
        value?.label === '' &&
        props.options?.length
      ) {
        const validOption = props.options.find(option => {
          if (isGroup(option)) return false;
          return option.value === value.value;
        });

        if (onChange && validOption && !isGroup(validOption)) {
          onChange(validOption, {action: 'select-option', option: validOption});
        }
      }
    },
    [onChange, props.options, props.value],
  );

  return (
    <ReactSelect
      ref={selectRef}
      empty={empty || !props.value}
      className={clsx(className, 'select', {
        'select--invalid': invalid || Boolean(error),
      })}
      classNamePrefix="select"
      error={error}
      label={label}
      invalid={invalid}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      isDisabled={disabled}
      isLoading={loading}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      isMenuOpen={isMenuOpen}
      filterOption={customFilter}
      showScrollbar={needShowScrollbar}
      isSearchable={isSearchableEnabled}
      components={{
        Menu,
        Input,
        Option,
        Control,
        MenuList,
        Placeholder,
        LoadingIndicator,
        DropdownIndicator,
        IndicatorSeparator: null,
        ...components,
      }}
      {...props}
    />
  );
}

type SelectForwardType = <T = undefined, V = string, L = string>(
  props: SelectProps<T, V, L> & {ref?: ForwardedRef<SelectRefType<T, V, L>>},
  // @ts-expect-error ts(2577)
) => ReturnType<typeof Select>;

const Select = forwardRef(SelectInner) as SelectForwardType;

export {Select};
export type {SelectRefType, SelectForwardType};
